import gql from 'graphql-tag';
import React from 'react';

import {ReactionHighlightQueryInput} from 'chemistry/ReactionChemSearchUtils';
import ReactionSchemeThumbnail from 'chemistry/reactions/ReactionSchemeThumbnail';
import {
  ReactionComponentRoleEnum,
  ReactionHighlightQueryFormatEnum,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {ChemistryReactionRole} from 'notebook/integrations/chemistry/ChemistryStructuredTableConstants';
import {CenteredSpinner} from 'ui-library/Spinner';
import {assertNever} from 'util/Types';

import type {
  HighlightedReactionSchemeThumbnailQuery,
  HighlightedReactionSchemeThumbnailQueryVariables,
} from './__generated/HighlightedReactionSchemeThumbnailQuery';

export const HIGHLIGHTED_REACTION_SCHEME_QUERY = gql`
  query HighlightedReactionSchemeThumbnailQuery(
    $reactionId: StrictString!
    $queries: [ReactionHighlightQueryInputType!]!
  ) @skipAudit @engComponent(name: CHEMISTRY) {
    reaction(id: $reactionId) {
      ... on ReactionNode {
        id
        highlightedScheme(queries: $queries) {
          base64Svg
        }
      }
    }
  }
`;

interface Props {
  reactionId: string;
  reactionName: string;
  /** Substructure queries to highlight, from the reaction search modal's clauses. */
  queries: Array<ReactionHighlightQueryInput>;
  /** The default (unhighlighted) scheme image URL, used as a fallback in every non-success case. */
  fallbackSrc: string;
}

function toHighlightQueryFormatEnum(
  format: ReactionHighlightQueryInput['format']
): ReactionHighlightQueryFormatEnum {
  switch (format) {
    case 'MOLFILE':
      return ReactionHighlightQueryFormatEnum.MOLFILE;
    case 'SMILES':
      return ReactionHighlightQueryFormatEnum.SMILES;
    default:
      return assertNever(format);
  }
}

function toReactionComponentRoleEnum(
  role: ChemistryReactionRole | null
): ReactionComponentRoleEnum | undefined {
  if (role == null) {
    return undefined;
  }
  switch (role) {
    case ChemistryReactionRole.REACTANT:
      return ReactionComponentRoleEnum.REACTANT;
    case ChemistryReactionRole.PRODUCT:
      return ReactionComponentRoleEnum.PRODUCT;
    case ChemistryReactionRole.CATALYST:
      return ReactionComponentRoleEnum.CATALYST;
    case ChemistryReactionRole.SOLVENT:
      return ReactionComponentRoleEnum.SOLVENT;
    case ChemistryReactionRole.BYPRODUCT:
      return ReactionComponentRoleEnum.BYPRODUCT;
    default:
      return assertNever(role);
  }
}

/** Converts the search-modal-facing query shape into the wire shape ReactionHighlightQueryInputType expects. */
function toGraphQLQueries(
  queries: Array<ReactionHighlightQueryInput>
): HighlightedReactionSchemeThumbnailQueryVariables['queries'] {
  return queries.map((query) => ({
    structure: query.structure,
    format: toHighlightQueryFormatEnum(query.format),
    reactionComponentRole: toReactionComponentRoleEnum(query.reactionComponentRole),
  }));
}

/**
 * Like ReactionSchemeThumbnail, but requests a version of the scheme with matched substructures
 * highlighted (see ReactionNode.highlightedScheme). Falls back to the plain ReactionSchemeThumbnail if
 * highlighting is disabled, not applicable, or fails for any reason - a missing highlight is a much smaller
 * regression than a broken or missing image, so this component never surfaces a distinct error state of its
 * own.
 */
export default function HighlightedReactionSchemeThumbnail({
  reactionId,
  reactionName,
  queries,
  fallbackSrc,
}: Props): JSX.Element {
  const alt = `Reaction scheme for ${reactionName}`;
  const result = useBenchlingQuery<
    HighlightedReactionSchemeThumbnailQuery,
    HighlightedReactionSchemeThumbnailQueryVariables
  >(HIGHLIGHTED_REACTION_SCHEME_QUERY, {
    variables: {reactionId, queries: toGraphQLQueries(queries)},
  });

  if (result.type === QueryResultType.PENDING) {
    return <CenteredSpinner />;
  }
  if (result.type === QueryResultType.ERROR) {
    return <ReactionSchemeThumbnail src={fallbackSrc} alt={alt} />;
  }

  const {reaction} = result.data;
  if (reaction == null || isInaccessible(reaction)) {
    return <ReactionSchemeThumbnail src={fallbackSrc} alt={alt} />;
  }
  const highlightedScheme = reaction.highlightedScheme;
  if (highlightedScheme?.base64Svg == null) {
    return <ReactionSchemeThumbnail src={fallbackSrc} alt={alt} />;
  }
  return (
    <ReactionSchemeThumbnail
      src={`data:image/svg+xml;base64,${highlightedScheme.base64Svg}`}
      alt={`${alt}, matched substructure highlighted`}
    />
  );
}
